/**
 * Shared Reputation Module — Edge Functions icin merkezi puan yonetimi
 * Tum puan, streak, bonus ve rozet islemleri bu modul uzerinden yapilir.
 */

// ============================================
// SABITLER
// ============================================

export const REPUTATION = {
  ON_TIME_PAYMENT: +3,
  EARLY_BIRD_PAYMENT: +2,
  LATE_PAYMENT: -2,
  VERY_LATE_PAYMENT: -5,
  PAYMENT_REJECTED: -8,
  CYCLE_COMPLETED: +5,
  LEFT_MID_CYCLE: -12,
  STREAK_BONUS: +3,
  PROFILE_COMPLETE: +5,
  REFERRAL_JOINED: +2,
  COMEBACK_BONUS: +3,
  AUTO_CONFIRM_RECEIVER_PENALTY: -5,
  FORCE_ADVANCE_PENALTY: -10,
} as const

export const BONUS_YEARLY_LIMIT = 15
export const COMEBACK_YEARLY_LIMIT = 2
export const STREAK_BONUS_INTERVAL = 3

/**
 * Dongu tarihi fonksiyonlari BURADA TANIMLI DEGIL.
 *
 * Bu modul uzun sure kendi kopyalarini tutuyordu ve kopyalar ayrismisti:
 * `isValidCycleDate` burada `getFullYear` (yerel saat), lifecycle.ts'te
 * `getUTCFullYear` kullaniyordu. Edge function'lar UTC'de kostugu icin
 * sahada ayni sonucu veriyorlardi — latent bir tuzak.
 *
 * Tek kaynak lifecycle.ts. Geriye donuk uyum icin buradan da ihrac edilir.
 */
import { calculateLateDays, advanceCycleDate } from './lifecycle.ts'
import { isDemoGroupName } from './demo.ts'
export { calculateLateDays, advanceCycleDate, isValidCycleDate } from './lifecycle.ts'

// ============================================
// PURE FONKSIYONLAR (DB bagimsiz)
// ============================================

/**
 * Demo gruplar gerçek itibarı değiştirmez. Kurallar: reputation-demo.deno.test.ts (RD-01…RD-03).
 *
 * Demo senaryolarda proje sahibi de koltukta; zamanlanmış görevler demo grupları
 * gerçek grup gibi işler. Puan, seri ve rozet çağrıları bu kontrolün arkasındadır;
 * bildirim ve tur ilerlemesi sürer. Grup okunamadıysa bugünkü davranış korunur.
 */
export function shouldAffectReputation(group: { name?: string | null } | null | undefined): boolean {
  return !isDemoGroupName(group?.name)
}

export function clampScore(current: number, change: number): number {
  return Math.max(0, Math.min(100, current + change))
}

/**
 * Odeme beyaninin erken (early-bird) olup olmadigini kontrol eder.
 * EarlyBird = beyan tarihi, donem tarihinden ONCE (strict before)
 */
export function isEarlyBird(declaredAt: Date, cycleDate: string): boolean {
  const declaredDay = declaredAt.toISOString().split('T')[0]
  return declaredDay < cycleDate
}

/**
 * Odeme puanini hesaplar: on-time, late, very-late
 */
export function calculatePaymentScore(declaredAt: Date, cycleDate: string): {
  score: number
  trigger: string
  isOnTime: boolean
} {
  const lateDays = calculateLateDays(declaredAt, cycleDate)

  if (lateDays === 0) {
    return { score: REPUTATION.ON_TIME_PAYMENT, trigger: 'on_time_payment', isOnTime: true }
  } else if (lateDays <= 3) {
    return { score: REPUTATION.LATE_PAYMENT, trigger: 'late_payment', isOnTime: false }
  } else {
    return { score: REPUTATION.VERY_LATE_PAYMENT, trigger: 'very_late_payment', isOnTime: false }
  }
}

/**
 * Belirli bir döngünün gerçek vade tarihini hesaplar.
 * Grup current_cycle_date'i, grubun MEVCUT döngüsüne ait.
 * Başka bir döngünün vadesini bulmak için offset gerekir.
 *
 * Örnek: current_cycle=1, current_cycle_date=2026-03-15, cycle_period=monthly
 *   → cycle 3 due date = 2026-05-15 (2 ay sonra)
 */
export function calculateCycleDueDate(
  baseCycleDate: string,
  baseCycleNumber: number,
  targetCycleNumber: number,
  cyclePeriod: 'monthly' | 'weekly',
  anchorDay?: number
): string {
  const offset = targetCycleNumber - baseCycleNumber
  if (offset === 0) return baseCycleDate

  // Ileri yon: motorun capali adimlamasi (ay sonu tasmasi yok).
  if (offset > 0) {
    let d = baseCycleDate
    for (let i = 0; i < offset; i++) d = advanceCycleDate(d, cyclePeriod, anchorDay)
    return d
  }

  // Geri yon: nadir (gecmis bir donemin vadesi). Motorda geri adimlama
  // yok; eski davranis korunuyor, ay sonu tasmasi bu dalda surer.
  const date = new Date(baseCycleDate + 'T00:00:00Z')
  if (cyclePeriod === 'monthly') {
    date.setUTCMonth(date.getUTCMonth() + offset)
  } else {
    date.setUTCDate(date.getUTCDate() + (offset * 7))
  }
  return date.toISOString().split('T')[0]
}

// ============================================
// DB FONKSIYONLARI (supabase client parametre olarak alinir)
// ============================================

/**
 * Kullanici itibar puanini gunceller ve activity_log kaydeder.
 * Tek kaynak (single source of truth) — tum puan islemleri buradan gecer.
 */
export async function updateReputation(
  supabase: any,
  userId: string,
  change: number,
  trigger: string,
  metadata: Record<string, unknown> = {}
): Promise<{ oldScore: number; newScore: number }> {
  if (!userId) return { oldScore: 0, newScore: 0 } // misafir koltuk: puanı yok
  const { data: user } = await supabase
    .from('users')
    .select('reputation_score')
    .eq('id', userId)
    .single()

  if (!user) {
    console.error(`User not found: ${userId}`)
    return { oldScore: 0, newScore: 0 }
  }

  const oldScore = user.reputation_score
  const newScore = clampScore(oldScore, change)

  await supabase
    .from('users')
    .update({ reputation_score: newScore })
    .eq('id', userId)

  await supabase.from('activity_logs').insert({
    user_id: userId,
    action: 'REPUTATION_CHANGE',
    metadata: { trigger, oldScore, newScore, change, ...metadata },
  })

  console.log(`  Score: ${userId.slice(0, 8)}... ${oldScore} -> ${newScore} (${change > 0 ? '+' : ''}${change}, ${trigger})`)
  return { oldScore, newScore }
}

/**
 * Streak arttirir, yeni streak degerini doner.
 */
export async function incrementStreak(supabase: any, userId: string): Promise<number> {
  if (!userId) return 0
  const { data: user } = await supabase
    .from('users')
    .select('current_streak')
    .eq('id', userId)
    .single()

  if (!user) return 0
  const newStreak = (user.current_streak || 0) + 1

  await supabase.from('users').update({ current_streak: newStreak }).eq('id', userId)
  console.log(`  Streak: ${userId.slice(0, 8)}... -> ${newStreak}`)
  return newStreak
}

/**
 * Streak sifirlar.
 */
export async function resetStreak(supabase: any, userId: string): Promise<void> {
  if (!userId) return
  await supabase.from('users').update({ current_streak: 0 }).eq('id', userId)
  console.log(`  Streak reset: ${userId.slice(0, 8)}...`)
}

/**
 * Bonus puan kullanilabilir mi kontrol eder (yillik limit).
 */
export async function canUseBonusPoints(supabase: any, userId: string, amount: number): Promise<boolean> {
  const { data: user } = await supabase
    .from('users')
    .select('bonus_points_used_this_year')
    .eq('id', userId)
    .single()

  const used = user?.bonus_points_used_this_year || 0
  return (used + amount) <= BONUS_YEARLY_LIMIT
}

/**
 * Bonus puan kullanimi kaydeder.
 */
export async function consumeBonusPoints(supabase: any, userId: string, amount: number): Promise<void> {
  const { data: user } = await supabase
    .from('users')
    .select('bonus_points_used_this_year')
    .eq('id', userId)
    .single()

  const used = user?.bonus_points_used_this_year || 0
  await supabase.from('users').update({ bonus_points_used_this_year: used + amount }).eq('id', userId)
}

/**
 * Comeback bonusu uygun mu kontrol eder (puan < 50 ve yillik limit)
 */
export async function canUseComeback(supabase: any, userId: string): Promise<boolean> {
  const { data: user } = await supabase
    .from('users')
    .select('comeback_count_this_year, reputation_score')
    .eq('id', userId)
    .single()

  if (!user) return false
  return (user.comeback_count_this_year || 0) < COMEBACK_YEARLY_LIMIT && user.reputation_score < 50
}

/**
 * Comeback bonusu kullanimi kaydeder.
 */
export async function consumeComeback(supabase: any, userId: string): Promise<void> {
  const { data: user } = await supabase
    .from('users')
    .select('comeback_count_this_year')
    .eq('id', userId)
    .single()

  const count = user?.comeback_count_this_year || 0
  await supabase.from('users').update({ comeback_count_this_year: count + 1 }).eq('id', userId)
}

/**
 * Onay/auto-confirm sonrasi streak bonusu, earlyBird ve comeback kontrolleri.
 * Sadece on-time odemelerde cagrilir.
 */
export async function processOnTimeRewards(
  supabase: any,
  userId: string,
  declaredAt: Date,
  cycleDate: string | null,
  meta: Record<string, unknown>
): Promise<{ streak: number; earlyBird: boolean; streakBonus: boolean; comeback: boolean }> {
  const result = { streak: 0, earlyBird: false, streakBonus: false, comeback: false }

  // Streak artir
  result.streak = await incrementStreak(supabase, userId)

  // EarlyBird kontrolu
  if (cycleDate && isEarlyBird(declaredAt, cycleDate)) {
    const bonusAmt = Math.abs(REPUTATION.EARLY_BIRD_PAYMENT)
    if (await canUseBonusPoints(supabase, userId, bonusAmt)) {
      await updateReputation(supabase, userId, REPUTATION.EARLY_BIRD_PAYMENT, 'early_bird_payment', meta)
      await consumeBonusPoints(supabase, userId, bonusAmt)
      result.earlyBird = true
    }
  }

  // Streak 3x bonus
  if (result.streak > 0 && result.streak % STREAK_BONUS_INTERVAL === 0) {
    const bonusAmt = Math.abs(REPUTATION.STREAK_BONUS)
    if (await canUseBonusPoints(supabase, userId, bonusAmt)) {
      await updateReputation(supabase, userId, REPUTATION.STREAK_BONUS, 'streak_bonus', { ...meta, streak: result.streak })
      await consumeBonusPoints(supabase, userId, bonusAmt)
      result.streakBonus = true
    }
  }

  // Comeback bonus (streak 3'e ulastiginda ve puan < 50 ise)
  if (result.streak === 3) {
    if (await canUseComeback(supabase, userId)) {
      await updateReputation(supabase, userId, REPUTATION.COMEBACK_BONUS, 'comeback_bonus', meta)
      await consumeComeback(supabase, userId)
      result.comeback = true
    }
  }

  return result
}

/**
 * Rozet kontrolu ve atama.
 * Her puan degisikliginden sonra cagrilir.
 */
export async function checkAndAwardBadges(
  supabase: any,
  userId: string,
  context: {
    streak?: number
    score?: number
    rank?: string
    isFirstPayment?: boolean
    cycleClean?: boolean
  } = {}
): Promise<string[]> {
  const awarded: string[] = []
  if (!userId) return awarded // misafir koltuk: rozet yok

  // Mevcut rozetleri al
  const { data: existingBadges } = await supabase
    .from('user_badges')
    .select('badge_slug')
    .eq('user_id', userId)

  const hasBadge = (slug: string) => existingBadges?.some((b: any) => b.badge_slug === slug) || false

  const badgesToCheck = [
    { slug: 'iyi_baslangic', condition: context.isFirstPayment === true },
    { slug: 'sozu_bir', condition: (context.streak || 0) >= 5 },
    { slug: 'dakik_dost', condition: (context.streak || 0) >= 10 },
    { slug: 'guven_abidesi', condition: (context.score || 0) >= 95 },
    { slug: 'altin_sozlu', condition: context.cycleClean === true },
    { slug: 'sozunun_eri', condition: context.rank === 'ustat' },
  ]

  for (const badge of badgesToCheck) {
    if (badge.condition && !hasBadge(badge.slug)) {
      const { error } = await supabase.from('user_badges').insert({
        user_id: userId,
        badge_slug: badge.slug,
        earned_at: new Date().toISOString(),
      })

      if (!error) {
        awarded.push(badge.slug)
        console.log(`  Badge awarded: ${badge.slug} -> ${userId.slice(0, 8)}...`)

        // Rozet kazanim bildirimi
        const { data: badgeInfo } = await supabase
          .from('badges')
          .select('name, icon')
          .eq('slug', badge.slug)
          .single()

        if (badgeInfo) {
          await supabase.from('notifications').insert({
            user_id: userId,
            type: 'badge_earned',
            title: `${badgeInfo.icon} Yeni Rozet: ${badgeInfo.name}`,
            body: `Tebrikler! "${badgeInfo.name}" rozetini kazandiniz!`,
            data: { badgeSlug: badge.slug },
          })
        }
      }
    }
  }

  return awarded
}

/**
 * Postaci bildirim gonderici (shared helper)
 */
export async function sendPostaciNotification(
  supabaseUrl: string,
  supabaseServiceKey: string,
  userId: string,
  templateSlug: string,
  variables: any
): Promise<void> {
  try {
    await fetch(`${supabaseUrl}/functions/v1/postaci-engine`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseServiceKey}`,
      },
      body: JSON.stringify({ user_id: userId, template_slug: templateSlug, variables }),
    })
  } catch (err) {
    console.error(`Postaci failed for ${userId}:`, err)
  }
}
